import { CustomField, FieldLabel } from '@measured/puck';
import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Toggle from '../../../components/common/toggleButton';
import CustomTooltip from '../../../components/common/tooltip';
import { Select } from '../../components/form-fields/Select';
import InputField from '../../../components/common/input-field';
import { Radio } from '../../components/form-fields/Radio';
import { Switch } from '@mui/material';

export type WidthAndHeightSubProps = {
  maxWidth: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
  height: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
  justifyContent?: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
  deviceType: string;
  fullWidth?: {
    [key: string]: boolean;
    mobile: boolean;
    tablet: boolean;
    desktop: boolean;
  };
};

interface Option {
  label: string;
  value: string;
}
interface RadioOption {
  id: string;
  name: string;
  value: boolean;
}

interface SelectField {
  type: 'select' | 'input';
  label: string;
  options?: Option[];
}

export interface WidthAndHeightField {
  maxWidth: SelectField;
  height: SelectField;
  justifyContent?: SelectField;
  fullWidth?: SelectField;
}

export interface IWidthAndHeightProps {
  title?: string;
  value: WidthAndHeightSubProps;
  onChange: (_value: WidthAndHeightSubProps) => void;
  isfullWidth?: boolean;
  WidthAndHeightField?: WidthAndHeightField;
}

const WidthAndHeight = ({
  value,
  onChange,
  title = 'Customize Image',
  isfullWidth = false,
  WidthAndHeightField = {
    maxWidth: {
      label: 'Max Width',
      type: 'input',
    },
    height: {
      label: 'Height',
      type: 'input',
    },
  },
}: IWidthAndHeightProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const handleChange = (key: string, option: string | number) => {
    onChange({
      ...value,
      [key]: {
        ...(value?.[key as keyof WidthAndHeightSubProps] as Record<
          string,
          number | string
        >),
        [value?.deviceType]: option,
      },
    });
  };

  return (
    <div>
      <div
        className="flex justify-between font-bold cursor-pointer bg-slate-100 rounded-md pagebuilder-theme"
        onClick={toggleAccordion}>
        <h3>{title}</h3>
        {!isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
      </div>
      {isOpen && (
        <div className="flex flex-col mt-3 items-center border border-gray-300 rounded p-2 w-full">
          <div className="w-full p-2">
            <Toggle<WidthAndHeightSubProps>
              value={value}
              onChange={newValue => {
                onChange({ ...(value || {}), ...newValue });
              }}
              heading={{ label: 'Device', value: 'device' }}
              options={[
                { label: 'Desktop', value: 'desktop' },
                { label: 'Tablet', value: 'tablet' },
                { label: 'Mobile', value: 'mobile' },
              ]}
              propName="deviceType"
            />
          </div>
          {Object.entries(WidthAndHeightField).map(([field, config]) => (
            <div key={field} className="p-2 w-full">
              {config.type === 'select' ? (
                <FieldLabel
                  label={
                    (
                      <CustomTooltip title={config?.label}>
                        <span>{config.label}</span>
                      </CustomTooltip>
                    ) as React.ReactNode as string
                  }>
                  <Select
                    value={
                      (
                        value[field as keyof WidthAndHeightSubProps] as Record<
                          string,
                          string
                        >
                      )[value.deviceType]
                    }
                    onChange={e => handleChange(field, e.target.value)}
                    options={config.options}
                  />
                </FieldLabel>
              ) : config.type === 'input' ? (
                <InputField
                  value={
                    (
                      value[field as keyof WidthAndHeightSubProps] as Record<
                        string,
                        string
                      >
                    )?.[value?.deviceType]
                  }
                  onChange={e => {
                    let newValue = e.replace(/[^0-9%]/g, '');

                    // Ensure that only one '%' symbol remains at the end
                    if (newValue?.includes('%')) {
                      newValue =
                        newValue.replace(/%.*%/g, '%').replace(/%/g, '') + '%';
                    }

                    // If the string is empty or contains only whitespace, set to 'auto'
                    if (!newValue.trim()) {
                      newValue = 'auto';
                    }

                    // If the value includes both digits and '%', clean up any non-digit/% characters
                    if (/\d/.test(newValue) && /%/.test(newValue)) {
                      newValue = newValue.replace(/[^0-9%]/g, '');
                    }

                    // Default to 'auto' if the value is still empty
                    if (!newValue.trim()) {
                      newValue = 'auto';
                    }

                    handleChange(field, newValue);
                  }}
                  field={config as CustomField<string>}
                />
              ) : (
                <Radio
                  value={
                    (
                      value?.[field as keyof WidthAndHeightSubProps] as Record<
                        string,
                        string
                      >
                    )?.[value?.deviceType]
                  }
                  onChange={e => handleChange(field, e.target.value)}
                  options={config.options as RadioOption[]}
                />
              )}
            </div>
          ))}
          {isfullWidth && (
            <div className="flex flex-row w-full justify-between items-center">
              <label className="text-sm font-medium ml-2">Full Width</label>
              <Switch
                className="flex justify-between items-center"
                checked={value?.fullWidth?.[value?.deviceType] || false}
                onChange={e => {
                  onChange({
                    ...(value || {}),
                    fullWidth: {
                      mobile: value?.fullWidth?.mobile ?? false,
                      tablet: value?.fullWidth?.tablet ?? false,
                      desktop: value?.fullWidth?.desktop ?? false,
                      [value?.deviceType]: e.target.checked,
                    },
                  });
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WidthAndHeight;
